import { Input, Stack } from '@etg/wings';
import { ReactComponent as F1ValidationIcon } from '@eti/assets/icons/revolut/exclamation-mark.svg';
import { useRevolut, useProperty, useTranslation } from '@eti/providers';
import type {
  ChangeEvent,
  ComponentPropsWithoutRef,
  MutableRefObject,
  ReactElement,
  ReactNode,
} from 'react';
import { forwardRef, useCallback, useMemo } from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { css } from '@eti/styles';
import { useControllerWithWarnings } from '../../../custom-hooks/useControllerWithWarnings';

const phoneInputGroupFieldsStyles = css`
  align-items: flex-end;
  position: relative;

  div:nth-child(2) {
    flex: 1;
  }
`;

const capitalize = (value: string) =>
  value[0] ? value[0].toLocaleUpperCase() + value.slice(1) : value;

interface FormInputProps extends ComponentPropsWithoutRef<'input'> {
  className?: string;
  countryCodeInput?: ReactNode;
  description?: string;
  format?: (value: any) => string;
  iconSuffix?: ReactElement;
  isRequired?: boolean;
  label?: string;
  labelRef?: MutableRefObject<HTMLLabelElement | null>;
  name: string;
  normalize?: (value: string) => any;
  onBlur?: (event: ChangeEvent<HTMLInputElement>) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  shouldForceCapitalize?: boolean;
  validations?: RegisterOptions;
  valueOverride?: string;
  warningValidation?: (value: string) => boolean | string;
}

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      className,
      countryCodeInput,
      description,
      format,
      iconSuffix,
      isRequired = false,
      label,
      labelRef,
      name,
      normalize,
      onBlur,
      onChange,
      onInput,
      placeholder,
      shouldForceCapitalize = false,
      validations,
      valueOverride,
      warningValidation,
      ...rest
    },
    ref,
  ) => {
    const { t, nonDebugT } = useTranslation();
    const { p } = useProperty();
    const { control } = useFormContext();
    const {
      field,
      fieldState: { error, warning },
    } = useControllerWithWarnings({ name, control, rules: validations, warningValidation });
    const { isRevolut } = useRevolut();

    const composeRefs = (node: HTMLInputElement) => {
      field.ref(node);

      const localRef = ref;
      if (localRef && 'current' in localRef) {
        localRef.current = node;
      }
    };

    const labelId = `${name}-label`;
    const errorId = `${name}-error`;
    const descriptionId = `${name}-description`;
    const resolvedLabel = useMemo(
      () => (label && (isRequired ? nonDebugT('Input.Field.Required', label) : label)) ?? '',
      [isRequired, label, nonDebugT],
    );

    const warningMessage = useMemo(() => (warning ? t(warning) : undefined), [warning, t]);

    const handleOnChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        let normalizedValue = e.target.value;
        if (shouldForceCapitalize) {
          normalizedValue = capitalize(normalizedValue);
        }
        if (normalize) {
          normalizedValue = normalize(normalizedValue);
        }

        field.onChange(normalizedValue);
        onChange?.(e);
      },
      [field, normalize, onChange, shouldForceCapitalize],
    );

    const handleOnBlur = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        field.onBlur();
        onBlur?.(e);
      },
      [field, onBlur],
    );

    const hasError = Boolean(error?.message);
    const formattedValue = format ? format(field.value) : field.value;
    const value = typeof valueOverride !== 'undefined' ? valueOverride : formattedValue;

    const inputField = (
      <Input
        {...field}
        aria-labelledby={`${labelId} ${errorId}`}
        data-testid={`${name}-input`}
        descriptionId={descriptionId}
        errorId={errorId}
        hasError={hasError}
        hasFloatingLabel={p('CustomerWeb.FloatingInputLabels.Enabled')}
        hasWarning={Boolean(warningMessage)}
        iconSuffix={iconSuffix}
        id={name}
        label={resolvedLabel}
        labelId={labelId}
        labelRef={labelRef}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
        onInput={onInput}
        placeholder={placeholder}
        ref={composeRefs}
        required={isRequired}
        value={value}
        {...rest}
      />
    );

    const phoneNumberLabel = t('PriceLock.Page.Form.PhoneNumber.Label');
    const phoneInputGroupLabel = isRequired
      ? nonDebugT('Input.Field.Required', phoneNumberLabel)
      : phoneNumberLabel;

    const phoneInputGroupFields = (
      <>
        <Input.Label id={`${name}-label`} htmlFor={name}>
          {phoneInputGroupLabel}
        </Input.Label>
        <Input.Group className={phoneInputGroupFieldsStyles}>
          {countryCodeInput}
          {inputField}
        </Input.Group>
      </>
    );

    return (
      <Stack className={className} spacing={4}>
        {countryCodeInput ? phoneInputGroupFields : inputField}
        <Input.Meta
          description={description}
          descriptionId={descriptionId}
          error={error?.message}
          errorId={errorId}
          hasError={hasError}
          hasWarning={Boolean(warningMessage)}
          iconPrefix={isRevolut ? <F1ValidationIcon /> : undefined}
          warning={warningMessage}
        />
      </Stack>
    );
  },
);

export default FormInput;
